import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { UserCheck, ArrowLeft, ArrowRight, Phone, Calendar, Clock, MapPin, Users } from 'lucide-react';
import Header from '../../common/Header';
import Footer from '../../common/Footer';

const BookingForm = () => {
  const { state } = useLocation();
  const navigate = useNavigate();
  const { doctor, slot, consultationType } = state || {};
  const [formData, setFormData] = useState({
    name: '',
    age: '',
    gender: '',
    phone: '',
    patient_address: '',
    care_of: '',
  });
  const [errors, setErrors] = useState({});

  const handleChange = e => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors({ ...errors, [name]: '' });
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.age) newErrors.age = 'Age is required';
    else if (formData.age < 1 || formData.age > 120) newErrors.age = 'Please enter a valid age';
    if (!formData.gender) newErrors.gender = 'Gender is required';
    if (!formData.phone.trim()) newErrors.phone = 'Phone number is required';
    else if (!/^\d{10}$/.test(formData.phone.replace(/\D/g, ''))) {
      newErrors.phone = 'Please enter a valid 10-digit phone number';
    }
    if (!formData.patient_address.trim()) newErrors.patient_address = 'Patient address is required';
    if (!formData.care_of.trim()) newErrors.care_of = 'Accompanying person name is required';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateForm()) {
      navigate('/doctor-checkout', { 
        state: { 
          doctor, 
          slot, 
          formData, 
          consultationType 
        } 
      });
    }
  };

  const handleBack = () => {
    navigate(-1);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    // Parse YYYY-MM-DD in local timezone by appending T00:00:00 (avoids UTC offset shift)
    const date = new Date(dateStr + 'T00:00:00');
    if (isNaN(date)) return dateStr;
    return date.toLocaleDateString('en-US', { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  const formatTime = (timeStr) => {
    if (!timeStr) return '';
    // Handle both "11:00 AM" and "11:00" (24-hour) formats
    let date = new Date(`2000-01-01 ${timeStr}`);
    if (isNaN(date)) {
      date = new Date(`2000-01-01T${timeStr}`);
    }
    if (isNaN(date)) return timeStr;
    return date.toLocaleTimeString('en-US', { 
      hour: 'numeric', 
      minute: '2-digit',
      hour12: true 
    });
  };

  if (!doctor || !slot) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 to-pink-100 flex items-center justify-center">
        <div className="text-center">
          <p className="text-xl text-red-600 mb-4">Invalid booking data.</p>
          <button 
            onClick={() => navigate('/')}
            className="text-blue-600 hover:text-blue-800"
          >
            Start over
          </button>
        </div>
      </div>
    );
  }

  return (
      <>
        <Header />
        
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 py-12">
      
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <button 
            onClick={handleBack}
            className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-800 transition-colors"
          >
            <ArrowLeft className="h-5 w-5" />
            Back to Slots
          </button>
        </div>

        <div className="bg-white rounded-3xl shadow-xl overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-8 py-8 text-white">
            <div className="flex items-center justify-center w-16 h-16 bg-white bg-opacity-20 rounded-full mx-auto mb-4">
              <UserCheck className="h-8 w-8" />
            </div>
            <h1 className="text-3xl font-bold text-center mb-4">Patient Details</h1>
            <p className="text-blue-100 text-center">
              Please provide your information to complete the booking
            </p>
          </div>

          {/* Appointment Summary */}
          <div className="px-8 py-6 bg-gray-50 border-b">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Appointment Summary</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                  <UserCheck className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-600">Doctor</p>
                  <p className="font-semibold">Dr. {doctor.name}</p>
                  <p className="text-sm text-gray-500">{doctor.specialization || doctor.specialty}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                  consultationType === 'priority' ? 'bg-purple-100' : 'bg-green-100'
                }`}>
                  <Calendar className={`h-5 w-5 ${
                    consultationType === 'priority' ? 'text-purple-600' : 'text-green-600'
                  }`} />
                </div>
                <div>
                  <p className="text-sm text-gray-600">
                    {consultationType === 'priority' ? 'Priority Appointment' : 'Normal Appointment'}
                  </p>
                  <p className="font-semibold">{formatDate(slot.date)}</p>
                  {slot.time && consultationType === 'priority' ? (
                    <p className="text-sm text-gray-500 flex items-center gap-1">
                      <Clock className="h-4 w-4" />
                      {formatTime(slot.time)}
                    </p>
                  ) : (
                    <p className="text-xs text-gray-500 italic">
                      Time based on arrival at hospital
                    </p>
                  )}
                </div>
              </div>
            </div>
            {consultationType === 'normal' && (
              <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-xs text-blue-800">
                  <strong>Note:</strong> This is a normal consultation. The doctor will see you based on your arrival time at the hospital (first come, first served).
                </p>
              </div>
            )}
          </div>

          {/* Form */}
          <div className="px-8 py-8">
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Full Name *
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${
                    errors.name ? 'border-red-500 bg-red-50' : 'border-gray-300'
                  }`}
                  placeholder="Enter your full name"
                />
                {errors.name && <p className="mt-1 text-sm text-red-600">{errors.name}</p>}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Age *
                  </label>
                  <input
                    type="number"
                    name="age"
                    value={formData.age}
                    onChange={handleChange}
                    min="1"
                    max="120"
                    className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${
                      errors.age ? 'border-red-500 bg-red-50' : 'border-gray-300'
                    }`}
                    placeholder="Age"
                  />
                  {errors.age && <p className="mt-1 text-sm text-red-600">{errors.age}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Gender *
                  </label>
                  <select
                    name="gender"
                    value={formData.gender}
                    onChange={handleChange}
                    className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${
                      errors.gender ? 'border-red-500 bg-red-50' : 'border-gray-300'
                    }`}
                  >
                    <option value="">Select Gender</option>
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                    <option value="Other">Other</option>
                  </select>
                  {errors.gender && <p className="mt-1 text-sm text-red-600">{errors.gender}</p>}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Phone Number *
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <Phone className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className={`w-full pl-12 pr-4 py-3 border rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${
                      errors.phone ? 'border-red-500 bg-red-50' : 'border-gray-300'
                    }`}
                    placeholder="Enter your phone number"
                  />
                </div>
                {errors.phone && <p className="mt-1 text-sm text-red-600">{errors.phone}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Patient Address *
                </label>
                <div className="relative">
                  <div className="absolute top-3 left-4 pointer-events-none">
                    <MapPin className="h-5 w-5 text-gray-400" />
                  </div>
                  <textarea
                    name="patient_address"
                    value={formData.patient_address}
                    onChange={handleChange}
                    rows={3}
                    className={`w-full pl-12 pr-4 py-3 border rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors resize-none ${
                      errors.patient_address ? 'border-red-500 bg-red-50' : 'border-gray-300'
                    }`}
                    placeholder="Enter patient's address"
                  />
                </div>
                {errors.patient_address && <p className="mt-1 text-sm text-red-600">{errors.patient_address}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Care Of (Accompanying Person) *
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <Users className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="text"
                    name="care_of"
                    value={formData.care_of}
                    onChange={handleChange}
                    className={`w-full pl-12 pr-4 py-3 border rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${
                      errors.care_of ? 'border-red-500 bg-red-50' : 'border-gray-300'
                    }`}
                    placeholder="e.g. Ramesh Kumar (Father)"
                  />
                </div>
                {errors.care_of && <p className="mt-1 text-sm text-red-600">{errors.care_of}</p>}
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="px-8 py-6 bg-gray-50 border-t">
            <button
              onClick={handleNext}
              className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-semibold py-4 px-6 rounded-xl transition-all duration-300 flex items-center justify-center gap-2 shadow-lg hover:shadow-xl"
            >
              Proceed to Checkout
              <ArrowRight className="h-5 w-5" />
            </button>
          </div>
        </div>
      </div>
    </div>
    <Footer />
      </>
  );
};

export default BookingForm;